import { isKnowRole, SPAN_ROLES, type SpanRole } from '@dispositio/core';
import { de } from '../i18n/de';

/** Eine Art der Verknüpfung samt Art des Bezugs: Nennung („Ist …“) oder Bezug („Gehört zu …“ als Rede, Handlung, …) */
export type LinkPreset = { kind: 'name' } | { kind: 'span'; role: SpanRole };

/**
 * Tastenkürzel je Art der Verknüpfung: Strg+⇧+1 „Ist …“, Strg+⇧+2 … 5 „Gehört zu …“ als Rede, Handlung, Gedanke, Beschreibung (in der
 * Reihenfolge von SPAN_ROLES). Strg+Alt geht nicht (auf deutschen Tastaturen AltGr, z. B. AltGr+Q = @); Strg+⇧+Ziffer ist weder im
 * Editor noch im Browser belegt. Gezählt wird die Taste, nicht das Zeichen (mit ⇧ wäre die 1 ein „!“). Die Bezüge des Wissens (erfährt,
 * enthüllt) haben kein Kürzel: Strg+⇧+6 setzt Zeitmarken, 7 … 9 sind Listen und Aufgaben.
 */
export const LINK_SHORTCUTS: { preset: LinkPreset; digit: number }[] = [
  { preset: { kind: 'name' }, digit: 1 },
  ...SPAN_ROLES.filter((r) => !isKnowRole(r)).map((role, i) => ({ preset: { kind: 'span', role } as LinkPreset, digit: i + 2 })),
];

/** Die Art zum Tastendruck (Strg bzw. ⌘ + ⇧ + Ziffer), sonst null */
export function linkPresetOf(e: { ctrlKey: boolean; metaKey: boolean; shiftKey: boolean; altKey: boolean; code: string }): LinkPreset | null {
  if (!(e.ctrlKey || e.metaKey) || !e.shiftKey || e.altKey) return null;
  return LINK_SHORTCUTS.find((s) => e.code === `Digit${s.digit}`)?.preset ?? null;
}

const digitOf = (preset: LinkPreset): number | null => LINK_SHORTCUTS.find((s) => s.preset.kind === preset.kind && (s.preset.kind === 'name' || (preset.kind === 'span' && s.preset.role === preset.role)))?.digit ?? null;

/** Das Kürzel als Text für Tooltips: „Strg+⇧+2“; null ohne Kürzel */
export const shortcutOf = (preset: LinkPreset): string | null => {
  const d = digitOf(preset);
  return d === null ? null : `Strg+⇧+${d}`;
};

/** Alle Kürzel untereinander, für Tooltips: „Ist … – Strg+⇧+1“, „Gehört zu … Rede – Strg+⇧+2“, … */
export const shortcutList = (): string =>
  LINK_SHORTCUTS.map((s) => `${s.preset.kind === 'name' ? de.refBrowser.kindName : `${de.refBrowser.kindSpan} ${de.refBrowser.roles[s.preset.role]}`} – ${shortcutOf(s.preset)}`).join('\n');
